// src/odoo/transactions_dashboard/pages/TransactionsDashboardPage.tsx
// All comments are in English.

import React from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import PageHeader from "@/components/shared/PageHeader";
import TransactionsDashboardFilterBar, {
  type TransactionsDashboardFilterState,
} from "../components/TransactionsDashboardFilterBar";
import RankingBarChart, {
  type RankingBarRow,
} from "../components/RankingBarChart";
import IntraBrandShareCard from "../components/IntraBrandShareCard";
import {
  useTopCustomers,
  useProductRankings,
  useIntraBrandShare,
} from "../hooks/useTransactionsDashboard";

const defaultDateRange = (): TransactionsDashboardFilterState => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 12);

  const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(end),
    companyId: 1,
  };
};

const formatWholeNumber = (value: number): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

// Shared currency formatter.
export const formatHKD = (
  value: number | string | null | undefined
): string => {
  let num: number;

  if (typeof value === "string") {
    const parsed = Number(value);
    num = Number.isFinite(parsed) ? parsed : 0;
  } else if (typeof value === "number") {
    num = Number.isFinite(value) ? value : 0;
  } else {
    num = 0;
  }

  return `HKD ${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

// Date formatter for drawer: "dd-MMM-YYYY", e.g. "01-Jan-2025".
export const formatDateDDMMMYYYY = (
  value: string | null | undefined
): string => {
  if (!value) return "-";

  const parts = value.slice(0, 10).split("-");
  if (parts.length !== 3) return value;

  const [year, monthStr, day] = parts;
  const monthIndex = Number(monthStr) - 1;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month =
    monthIndex >= 0 && monthIndex < monthNames.length
      ? monthNames[monthIndex]
      : monthStr;

  const paddedDay = day.padStart(2, "0");
  return `${paddedDay}-${month}-${year}`;
};

export type QuotationSortKey =
  | "order_name"
  | "partner_name"
  | "amount_total"
  | "created_on"
  | "salesperson_name";

export type SortDirection = "asc" | "desc";

const TransactionsDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] =
    React.useState<TransactionsDashboardFilterState>(defaultDateRange);

  const baseApiFilters = React.useMemo(
    () => ({
      startDate: filters.startDate,
      endDate: filters.endDate,
      companyId: filters.companyId,
      activeOnly: filters.activeOnly ?? false,
      limit: 10,
    }),
    [filters]
  );

  const productRankingFilters = React.useMemo(
    () => baseApiFilters,
    [baseApiFilters]
  );

  const topCustomersQuery = useTopCustomers(baseApiFilters);
  const productRankingsQuery = useProductRankings(productRankingFilters);
  const intraBrandShareQuery = useIntraBrandShare(productRankingFilters);

  const handleCustomerBarClick = React.useCallback(
    (row: RankingBarRow) => {
      if (!row.id) return;
      navigate(`/odoo/customer/${row.id}`);
    },
    [navigate]
  );

  const customerRows = React.useMemo<RankingBarRow[]>(
    () =>
      (topCustomersQuery.data ?? []).map((row) => ({
        id: row.partner_id,
        label: row.partner_name,
        value: Number(row.total_billed ?? 0),
        secondaryLabel: "Outstanding",
        secondaryValue: Number(row.pending_to_pay ?? 0),
      })),
    [topCustomersQuery.data]
  );

  const quantityRows = React.useMemo<RankingBarRow[]>(
    () =>
      (productRankingsQuery.data?.by_quantity ?? []).map((row) => ({
        id: row.product_model,
        label: row.product_model,
        value: Number(row.total_quantity ?? 0),
        secondaryLabel: "Billing",
        secondaryValue: Number(row.total_revenue ?? 0),
      })),
    [productRankingsQuery.data]
  );

  const revenueRows = React.useMemo<RankingBarRow[]>(
    () =>
      (productRankingsQuery.data?.by_revenue ?? []).map((row) => ({
        id: row.product_model,
        label: row.product_model,
        value: Number(row.total_revenue ?? 0),
        secondaryLabel: "Quantity",
        secondaryValue: Number(row.total_quantity ?? 0),
      })),
    [productRankingsQuery.data]
  );

  return (
    <div className="relative space-y-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-[radial-gradient(circle_at_top_left,_rgba(15,23,42,0.18),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(245,158,11,0.12),_transparent_28%),linear-gradient(to_bottom,_rgba(248,250,252,0.95),_transparent)]" />
      <PageHeader
        title="Sales Ranking Insights"
        description="Customer billing leaderboard and product ranking analysis."
      />

      <Card className="border-slate-200/70 bg-white/85 shadow-sm backdrop-blur">
        <div className="border-b border-slate-200/70 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                Filters
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Change the date window or company scope for the billing
                leaderboard.
              </p>
            </div>
          </div>
        </div>
        <div className="px-5 py-4">
          <TransactionsDashboardFilterBar
            value={filters}
            onChange={setFilters}
          />
        </div>
      </Card>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <RankingBarChart
          title="Customer Billing Leaderboard"
          description="Top 10 companies by billed amount for the selected period."
          rows={customerRows}
          valueLabel="Total billed"
          valueFormatter={formatHKD}
          secondaryValueFormatter={formatHKD}
          barColor="#0f766e"
          isLoading={topCustomersQuery.isLoading}
          error={topCustomersQuery.error ?? null}
          emptyMessage="No customer billing data for this period."
          onBarClick={handleCustomerBarClick}
        />

        <div className="rounded-3xl border border-slate-200/70 bg-white/85 p-5 shadow-sm backdrop-blur">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                Hot Product Rankings
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Uses shared product-model mapping and ranks by quantity/billing.
                This panel follows the selected date range.
              </p>
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-600">
              <span>Active licenses only</span>
              <span className="text-xs text-slate-400">
                {filters.activeOnly ? "On" : "Off"}
              </span>
            </div>
          </div>

          <Tabs defaultValue="quantity" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 bg-slate-100">
              <TabsTrigger value="quantity">Group by quantity</TabsTrigger>
              <TabsTrigger value="revenue">Group by revenue</TabsTrigger>
            </TabsList>

            <TabsContent value="quantity" className="mt-0">
              <RankingBarChart
                title="Top products by quantity"
                description="Products with the highest quantity."
                showHeader={false}
                rows={quantityRows}
                valueLabel="Quantity"
                valueFormatter={formatWholeNumber}
                secondaryValueFormatter={formatHKD}
                barColor="#b45309"
                isLoading={productRankingsQuery.isLoading}
                error={productRankingsQuery.error ?? null}
                emptyMessage="No active-license quantity data available."
              />
            </TabsContent>

            <TabsContent value="revenue" className="mt-0">
              <RankingBarChart
                title="Top products by revenue"
                description="Products generating the highest billing."
                showHeader={false}
                rows={revenueRows}
                valueLabel="Billing"
                valueFormatter={formatHKD}
                secondaryValueFormatter={formatWholeNumber}
                barColor="#1d4ed8"
                isLoading={productRankingsQuery.isLoading}
                error={productRankingsQuery.error ?? null}
                emptyMessage="No active-license billing data available."
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <IntraBrandShareCard
        brands={intraBrandShareQuery.data?.brands ?? []}
        isLoading={intraBrandShareQuery.isLoading}
        error={intraBrandShareQuery.error ?? null}
      />
    </div>
  );
};

export default TransactionsDashboardPage;
